export enum OrderStatus {
  PENDING_CONFIRMATION = 'PENDING_CONFIRMATION', // Chờ duyệt
  CONFIRMED = 'CONFIRMED', // Duyệt thành công
  UNCONFIRMED = 'UNCONFIRMED', // Từ chối
  CANCELLED = 'CANCELLED', // Đã hủy
  ALL = 'ALL', // Đã hủy
}

export enum PaymentMethod {
  DEPOSIT = 'deposit',
  CREDIT = 'credit',
  CKTM = 'cktm',
}

export enum PassengerType {
  ADT = 'ADT', // Người lớn
  CHD = 'CHD', // Trẻ em
  INF = 'INF', // Trẻ sơ sinh
}

export enum DocumentType {
  CCCD = 'CCCD',
  PASSPORT = 'PASSPORT',
}

export enum FlightType {
  ONE_WAY = 'ONE_WAY',
  ROUND_TRIP = 'ROUND_TRIP',
}

export interface Airport {
  code: string;
  name: string;
  cityName: string;
  terminal?: string;
}

export interface Flight {
  departure: string; // Airport code
  arrival: string; // Airport code
  departureTime: string; // ISO datetime
  arrivalTime: string; // ISO datetime
  flightNumber: string;
  carrier?: string; // Default: 9G
  cabinClass?: string; // Economy/Business
  fareFamilyCode?: string;
}

export interface FareRule {
  orderId?: number;
  fareRuleName: string;
  type: 0 | 1 | 2; // 0: Allowed, 1: Fee, 2: Not allowed
  value?: string | null;
}

export interface Ticket {
  name: string;
  quantity: number;
  imageUrl?: string;
}

export interface Passenger {
  id: string;
  title: string; // Mr/Mrs/Ms
  firstName: string;
  lastName: string;
  passengerType: PassengerType;
  dateOfBirth: string; // YYYY-MM-DD
  documentType: DocumentType;
  documentNumber: string;
  nationality?: string;
  guardianName?: string; // For INF only
  eTicketNumber?: string; // After payment
}

export interface ContactInfo {
  fullName: string;
  email: string;
  phoneCode: string;
  phoneNumber: string;
}

export interface OrderItem {
  combo: {
    quantity: number;
    name?: string;
    comboName?: string;
    salePrice: number;
  };
  flight: Flight;
  tickets: Ticket[];
}

export interface HolidayOrder {
  id: string;
  orderCode: string;
  submitNumber?: string;
  createdAt: string; // ISO datetime
  paymentMethod: PaymentMethod;
  thirdPartySubmitNumber: string; // Mã đặt đơn từ hệ thống thứ 3 (UUID)
  orderItems: OrderItem[];
  totalPayment: number;
  expiredAt: string; // ISO datetime
  agent: string; // Người đặt đơn
  subOrderState: OrderStatus;
  agentConfirmed?: string; // Người phê duyệt
  confirmedAt?: any; // Thời gian phê duyệt
  logConfirmation?: any; // Thời gian phê duyệt
}

export interface OrderDetail extends HolidayOrder {
  pnr: string;
  flightType: FlightType;
  passengers?: Passenger[];
  contactInfo?: ContactInfo;
  fareRules?: FareRule[];
  termsAndConditionsUrl?: string;
}

export interface OrderFilterParams {
  orderDate?: string; // YYYY-MM-DD
  status?: OrderStatus | '';
  orderCode?: string;
  paymentMethod?: PaymentMethod | '';
  agent?: string;
  page?: number;
  pageSize?: number;
}

export interface OrderListResponse {
  data: HolidayOrder[];
  total: number;
  page: number;
  pageSize: number;
}

// API Response Interfaces (actual backend structure)
export interface ApiOrderState {
  value: string; // 'cancelled', 'pendingConfirmation', 'confirmed', 'unconfirmed'
  name: string; // 'Đã hủy', 'Chờ duyệt', etc.
}

export interface ApiBusinessPartner {
  id: number;
  name: string;
  fullName: string;
  code: string;
  phone: string;
  bpLongTerm: string;
  allowBuyShortTerm: boolean;
  allowBuyPaperTicket: boolean;
  allowReturnWithoutPolicy: boolean;
}

export interface ApiAgent {
  id: number;
  username: string;
  name: string;
  address: string | null;
  phone: string;
  gender: string;
}

export interface ApiPayment {
  id: number;
  code: string; // 'Deposit', 'Credit', 'CKTM'
  name: string;
  description: string | null;
  mapVGS: string;
  validFrom: string;
  validTo: string;
  weight: number;
  default: string[];
}

export interface ApiOrderItems {
  id: number;
  productName: string;
  productCode: string;
}

export interface ApiFlight {
  flightId: string;
  flightNumber: string;
  departure: string; // Airport code
  arrival: string; // Airport code
  departureTime: string; // "05:45:00 12/02/2026"
  arrivalTime: string; // "07:45:00 12/02/2026"
}

export interface ApiPark {
  id: number;
  name: string;
  workstationId: string;
}

export interface ApiTicket {
  ticketId: string;
  name: string;
  quantity: number;
  siteCode: string;
  otaProductCode: string;
  adultQuantity: number;
  childQuantity: number;
  infantQuantity: number;
  usingDate: string; // "00:00:00 11/02/2026"
  usingDateTo: string; // "00:00:00 17/02/2026"
  park: ApiPark;
}

export interface ApiHolidayOrder {
  subOrderState: ApiOrderState;
  id: number;
  totalPrice: string;
  totalTicket: number;
  paymentStatus: string; // 'pending', 'paid'
  idHash: string;
  ticketChannel: string; // 'b2b'
  orderType: string; // 'new'
  holidaySubmitNumber: string; // Order code/UUID
  sapSubmitNumber: string | null;
  thirdPartySubmitNumber: string;
  orderDate: string; // "10:47:37 11/02/2026"
  expiredAt: string; // "11:17:50 11/02/2026"
  orderState: ApiOrderState;
  invoiceStatus: string;
  seq: number | null;
  orderSource: string | null;
  canceller: string | null;
  businessPartner: ApiBusinessPartner;
  agent: ApiAgent;
  payment: ApiPayment;
  orderSourceName: string;
  items: ApiOrderItems;
  flights: ApiFlight[];
  tickets: ApiTicket[];
}

export interface ApiPaginator {
  last: number;
  current: number;
  numItemsPerPage: number;
  first: number;
  pageCount: number;
  totalCount: number;
  pageParameterName: string;
  sortFieldParameterName: string;
  sortDirectionParameterName: string;
  filterFieldParameterName: string;
  filterValueParameterName: string;
  distinct: boolean;
  pageOutOfRange: string;
  defaultLimit: number;
  pagesInRange: number[];
  firstPageInRange: number;
  lastPageInRange: number;
  currentItemCount: number;
  firstItemNumber: number;
  lastItemNumber: number;
}

export interface ApiHolidayOrderListResponse {
  data: ApiHolidayOrder[];
  paginator: ApiPaginator;
}
